import React, { FC } from 'react';
import {
  IconCurrentOnlyOutline,
  IconSideBySideOutline,
  IconPlusOutline,
  IconMinusOutline,
  IconFitTo100Outline,
  IconFitToScreenOutline,
  IconSyncActionsOutline,
  IconHighlightDiffsOutline,
  IconHideDiffsOutline,
  IconViewCurrentOutline,
  IconViewBaselineOutline,
} from '@saucelabs/ds-icons';

import { DiffViewMenuButton } from './DiffViewMenuButton';
import { useVisualDispatch, useVisualSelector } from '../../../reduxHooks';
import { actions, selectors, constants } from '../../../ducks/diffUi';
import KeyBind from '../../common/KeyBind';
import { DiffUiDisplayType } from '../../../ducks/diffUi/constants';
import { ImageType } from '../../../global/constants';

const Divider = () => (
  <div
    className="border-0 border-b border-solid border-gray-200 w-full"
    role="presentation"
  />
);

export const DiffViewMenu: FC = () => {
  const dispatch = useVisualDispatch();

  const handleDisplayToggle = () => {
    dispatch(actions.toggleDisplayType());
    dispatch(actions.zoomToFit());
  };
  const handleZoomIn = () => dispatch(actions.zoomIn());
  const handleZoomOut = () => dispatch(actions.zoomOut());
  const handleZoomReset = () => dispatch(actions.resetZoom());
  const handleZoomToFit = () => dispatch(actions.zoomToFit(true));
  const handlePanSyncToggle = () => dispatch(actions.togglePanSync());
  const handleEmphasizeDiffs = () => dispatch(actions.toggleEmphasizeDiffs());
  const handleHideDiffs = () => dispatch(actions.toggleHideDiffs());
  const handleToggleSingleView = () => dispatch(actions.toggleSingleView());
  const {
    syncPan,
    zoom,
    displayType,
    diffSettings,
    singleDisplayType,
  } = useVisualSelector(selectors.getDiffUi);

  return (
    <div className="shadow-md bg-white rounded flex flex-col flex-nowrap">
      <KeyBind
        char={['v']}
        func={handleDisplayToggle}
        label={displayType === DiffUiDisplayType.Single ? 'View Side by Side' : 'View Current'}
        placement="left"
      >
        <DiffViewMenuButton
          icon={displayType === DiffUiDisplayType.Single ? (
            IconCurrentOnlyOutline
          ) : IconSideBySideOutline}
          label={displayType === DiffUiDisplayType.Single ? 'View Side by Side' : 'View Current'}
          onPress={handleDisplayToggle}
        />
      </KeyBind>
      <Divider />
      <KeyBind
        char={['+', 'shift|+', '=']}
        displayChar="="
        func={handleZoomIn}
        label="Zoom In"
        placement="left"
      >
        <DiffViewMenuButton
          icon={IconPlusOutline}
          isDisabled={zoom >= constants.MAX_ZOOM_IN}
          label="Zoom In"
          onPress={handleZoomIn}
        />
      </KeyBind>
      <KeyBind
        char={['-', '_|shift', '_']}
        displayChar="-"
        func={handleZoomOut}
        label="Zoom Out"
        placement="left"
      >
        <DiffViewMenuButton
          icon={IconMinusOutline}
          isDisabled={zoom <= constants.MAX_ZOOM_OUT}
          label="Zoom Out"
          onPress={handleZoomOut}
        />
      </KeyBind>
      <KeyBind
        char="0"
        func={handleZoomReset}
        label="Zoom to 100%"
        placement="left"
      >
        <DiffViewMenuButton
          icon={IconFitTo100Outline}
          isHidden={zoom === 1}
          label="Zoom to 100%"
          onPress={handleZoomReset}
        />
      </KeyBind>
      <KeyBind
        char="1"
        func={handleZoomToFit}
        label="Zoom to Fit"
        placement="left"
      >
        <DiffViewMenuButton
          icon={IconFitToScreenOutline}
          isHidden={zoom !== 1}
          label="Zoom to Fit"
          onPress={handleZoomToFit}
        />
      </KeyBind>
      <Divider />
      <KeyBind
        char="e"
        func={handleEmphasizeDiffs}
        label="Emphasize Diffs"
        placement="left"
      >
        <DiffViewMenuButton
          icon={IconHighlightDiffsOutline}
          isActive={diffSettings.emphasizeDiffs}
          label="Emphasize Diffs"
          onPress={handleEmphasizeDiffs}
        />
      </KeyBind>
      <KeyBind
        char="h"
        func={handleHideDiffs}
        label="Hide Diffs"
        placement="left"
      >
        <DiffViewMenuButton
          icon={IconHideDiffsOutline}
          isActive={diffSettings.hideDiffs}
          label="Hide Diffs"
          onPress={handleHideDiffs}
        />
      </KeyBind>
      {displayType === DiffUiDisplayType.SideBySide && (
        <>
          <Divider />
          <KeyBind
            char="p"
            func={handlePanSyncToggle}
            label="Sync Pan & Zoom"
            placement="left"
          >
            <DiffViewMenuButton
              icon={IconSyncActionsOutline}
              isActive={syncPan}
              label="Sync Pan & Zoom"
              onPress={handlePanSyncToggle}
            />
          </KeyBind>
        </>
      )}
      {displayType === DiffUiDisplayType.Single && (
        <>
          <Divider />
          <KeyBind
            char="t"
            func={handleToggleSingleView}
            label={singleDisplayType === ImageType.BASELINE ? (
              'View Current'
            ) : 'View Baseline'}
            placement="left"
          >
            <DiffViewMenuButton
              icon={singleDisplayType === ImageType.BASELINE ? (
                IconViewBaselineOutline
              ) : IconViewCurrentOutline}
              isActive={syncPan}
              label={singleDisplayType === ImageType.BASELINE ? (
                'View Current'
              ) : 'View Baseline'}
              onPress={handleToggleSingleView}
            />
          </KeyBind>
        </>
      )}
    </div>
  );
};
